import { useEffect, useRef, useState } from 'react'

import { LayoutWorkerClient } from '../layout/LayoutWorkerClient.js'
import { migrateProjectDocument } from '../project/migrateProjectDocument.js'
import type { ProjectDocumentV2 } from '../project/ProjectDocument.js'
import { createRenderBackend } from '../render/createRenderBackend.js'
import { detectDeviceClass, readBrowserDeviceClassSignals } from './detectDeviceClass.js'
import {
  initialTransientFromLegacy,
  transientFromLegacyPreservingPlayback,
  type LegacyV2State,
} from './legacyPlaybackBridge.js'
import { createLegacyProjectBridgeGate, type LegacyProjectBridgeGate } from './legacyProjectBridge.js'
import { createV2Runtime, type LayoutOffsetTarget, type V2Runtime } from './V2Runtime.js'

export interface UseV2RuntimeOptions {
  readonly enabled: boolean
  readonly legacyState: LegacyV2State
  readonly initialProject?: ProjectDocumentV2
  readonly readyToStart?: boolean
  readonly resolveFontSource?: (fontName: string) => string | undefined
  readonly onPlaybackComplete?: (progress: number) => void
  readonly onStaleLayoutOffsets?: (targets: readonly LayoutOffsetTarget[]) => void
}

function productionRuntime(
  initialProject: ProjectDocumentV2,
  onPlaybackComplete: (progress: number) => void,
  onStaleLayoutOffsets: (targets: readonly LayoutOffsetTarget[]) => void,
  resolveFontSource?: (fontName: string) => string | undefined,
): V2Runtime {
  const layoutClient = new LayoutWorkerClient({
    createWorker: () => new Worker(
      new URL('../workers/layout.worker.ts', import.meta.url),
      { type: 'module' },
    ),
  })
  const animationFrames = {
    request: (callback: (timestampMs: number) => void) => window.requestAnimationFrame(callback),
    cancel: (handle: number) => { window.cancelAnimationFrame(handle) },
  }
  const clock = { now: () => performance.now() }
  return createV2Runtime({
    initialProject,
    layoutClient,
    createBackend: (preferred, canvas2dPresentation) => createRenderBackend({
      preferred,
      ...(canvas2dPresentation === null ? {} : { canvas2dPresentation }),
    }),
    raf: animationFrames,
    clock,
    deviceClass: detectDeviceClass(readBrowserDeviceClassSignals()),
    onPlaybackComplete,
    onStaleLayoutOffsets,
    ...(resolveFontSource === undefined ? {} : { resolveFontSource }),
  })
}

/** The only bridge from mutable V1 React state into the shared V2 engine. */
export function useV2Runtime({
  enabled,
  legacyState,
  initialProject,
  readyToStart = true,
  resolveFontSource,
  onPlaybackComplete,
  onStaleLayoutOffsets,
}: UseV2RuntimeOptions): V2Runtime | null {
  const [runtime, setRuntime] = useState<V2Runtime | null>(null)
  const completionReference = useRef(onPlaybackComplete)
  const staleOffsetsReference = useRef(onStaleLayoutOffsets)
  const projectBridgeReference = useRef<LegacyProjectBridgeGate | null>(null)
  completionReference.current = onPlaybackComplete
  staleOffsetsReference.current = onStaleLayoutOffsets

  useEffect(() => {
    if (!enabled) {
      setRuntime(null)
      return
    }
    const ownedInitialProject = initialProject ?? migrateProjectDocument({ version: 1, ...legacyState })
    const nextRuntime = productionRuntime(
      ownedInitialProject,
      (progress) => { completionReference.current?.(progress) },
      (targets) => { staleOffsetsReference.current?.(targets) },
      resolveFontSource,
    )
    projectBridgeReference.current = createLegacyProjectBridgeGate(ownedInitialProject)
    nextRuntime.syncTransient(initialTransientFromLegacy(legacyState))
    setRuntime(nextRuntime)
    return () => {
      projectBridgeReference.current = null
      nextRuntime.dispose()
    }
    // Creation is scoped only to the migration switch. Later state is synced below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  useEffect(() => {
    if (runtime === null || !readyToStart) return
    void runtime.start()
  }, [readyToStart, runtime])

  useEffect(() => {
    if (runtime === null) return
    const legacyProject = migrateProjectDocument({ version: 1, ...legacyState })
    if (projectBridgeReference.current?.accept(legacyProject) === true) {
      runtime.syncProject(legacyProject)
    }
    runtime.syncTransient(transientFromLegacyPreservingPlayback(
      legacyState,
      runtime.store.getSnapshot().transient.playback,
    ))
  }, [runtime, legacyState])

  useEffect(() => {
    if (runtime === null || typeof document === 'undefined') return
    const updateVisibility = () => { runtime.setPageVisible(document.visibilityState !== 'hidden') }
    updateVisibility()
    document.addEventListener('visibilitychange', updateVisibility)
    return () => { document.removeEventListener('visibilitychange', updateVisibility) }
  }, [runtime])

  useEffect(() => {
    if (runtime === null || typeof window === 'undefined') return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updateMotion = () => { runtime.setReducedMotion(media.matches) }
    updateMotion()
    media.addEventListener('change', updateMotion)
    return () => { media.removeEventListener('change', updateMotion) }
  }, [runtime])

  return runtime
}
